import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useReactiveStore } from "@ts-private/reactive-store";
import { useListBrowserUtils } from "@ts-private/list-browser";
import { Autocomplete, TextField } from "@totalsoft/rocket-ui";
import { Grid, FormControlLabel, Checkbox } from "@mui/material";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { PARTNERS_SEARCH } from "../queries";

const HierarchiesListFilters = ({ store }) => {
  const { t } = useTranslation();
  const [$store] = useReactiveStore(store);
  const { updateFilter } = useListBrowserUtils(store);
  const [partnerInputValue, setPartnerInputValue] = useState("");

  const { data, loading } = useQueryWithSimpleErrorHandling(PARTNERS_SEARCH, {
    variables: { search: partnerInputValue }
  });

  const partnerOptions = useMemo(
    () => data?.partnersSearch.map(x => ({ id: x.id, partnerName: x.partnerName + " - " + x.uniqueId })) || [],
    [data?.partnersSearch]
  );

  const partnerValue = useMemo(
    () => partnerOptions.find(x => x.id === $store.filters.partnerId),
    [$store.filters.partnerId, partnerOptions]
  );

  const handleInputChange = useCallback((_, value, reason) => {
    if (reason !== "reset") setPartnerInputValue(value);
  }, []);

  const handlePartnerIdChange = useCallback(
    (value) => {
      updateFilter("partnerId")(value?.id);
      setPartnerInputValue(value?.partnerName || "");
    },[updateFilter]
  )


  return (
    <Grid container spacing={2} alignItems={"center"}>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label={t("PartnerHierarchy.HierarchyName")}
          value={$store.filters.hierarchyName || ""}
          onChange={updateFilter("hierarchyName")}
          inputProps={{ maxLength: 255 }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label={t("PartnerHierarchy.HierarchyCode")}
          value={$store.filters.hierarchyCode || ""}
          onChange={updateFilter("hierarchyCode")}
          inputProps={{ maxLength: 255 }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          fullWidth
          inputValue={partnerInputValue}
          onInputChange={handleInputChange}
          value={partnerValue}
          onChange={handlePartnerIdChange}
          label={t("PartnerHierarchy.Partner")}
          valueKey="id"
          labelKey="partnerName"
          options={partnerOptions}
          isClearable={true}
          loading={loading}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={$store.filters.isActive}
              indeterminate={$store.filters.isActive === null}
              onChange={event => updateFilter("isActive")($store.filters.isActive === false ? null : event.target.checked)}
              color="primary"
            />
          }
          label={t("PartnerHierarchy.IsActive")}
        />
      </Grid>
    </Grid>
  );
};

HierarchiesListFilters.propTypes = {
  store: PropTypes.object.isRequired
};

export default HierarchiesListFilters;
